import { useApi } from '@/components/Functional/hooks-core/useApi'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import { useTanstackStampDetailFetchFn } from '@/components/Functional/hooks-core/useTanstackFetchFn'
import { ApiError } from '@/components/Functional/lib/request'
import { useStampPointLabelFromSheetTypes, type StampPointType } from '@/components/Functional/useStampPointLabel'
import { time } from '@/components/Functional/utils/util'
import { BottomNavigation } from '@/components/Layout/BottomNavigation'
import { Header } from '@/components/Layout/Header'
import { ScanCodeOverlay } from '@/components/Layout/scanner/components/ScanCodeOverlay'
import { ArPhotoOverlay } from '@/components/Models/ar-photo'
import { ActionDialog } from '@/components/Models/dialog/components/ActionDialog'
import { DialogLoginRequired } from '@/components/Models/dialog/components/DialogLoginRequired'
import { DialogUseCoupon } from '@/components/Models/dialog/components/DialogUseCoupon'
import { OcrFrame } from '@/components/Pages/ocr/components'
import {
  DialogArPhotoLocked,
  DialogAudioGuideLocked,
  DialogGetStamps,
  DialogQuizChallenge,
  DialogQuizKeyword,
  DialogQuizResult,
  DialogRedisplayStamps,
  DialogSpotsAudioGuide,
  SpotDetail,
} from '@/components/Pages/spots/components'
import { SpotDetailSkeleton } from '@/components/Pages/spots/components/detail/SpotDetailSkeleton'
import { SpotDetailStateMachineDebug } from '@/components/Pages/spots/components/detail/SpotDetailStateMachineDebug'
import { DialogCouponUsed } from '@/components/Pages/spots/components/dialogs/DialogCouponUsed'
import { DialogOcrReadFailure } from '@/components/Pages/spots/components/dialogs/DialogOcrReadFailure'
import { DialogOcrWarning } from '@/components/Pages/spots/components/dialogs/DialogOcrWarning'
import { Button } from '@/components/UI/Button'
import { Snackbar } from '@/components/UI/Snackbar'
import {
  type PhotoFrameItem,
  type SpotCouponEntryData,
  type SpotCouponsResponse,
  isAudioGuideLocked,
  isPhotoFrameLocked,
  toAudioGuideDialogItems,
  toPhotoFrameItems,
} from '@/schemas/v2/spot'
import { useTokenStore } from '@/store/useTokenStore'
import { extractQrHash } from '@/lib/qrUtils'
import { Loader } from '@mantine/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSelector } from '@xstate/react'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSpotDetailStateMachine } from './useSpotDetailStateMachine'
import { getActiveStamp } from './stampUtils'

/**
 * スタンプ獲得 API エラーコードを i18n キーに変換する。
 * 対応していないコードはデフォルトの汎用キーを返す。
 */
const resolveAcquireErrorKey = (code: string | null, stampPointType: StampPointType = 'STAMP'): string => {
  switch (code) {
    case 'STAMP_ALREADY_ACQUIRED':
      if (stampPointType === 'POINT') return 'spot_detail.acquire_error.point_already_acquired'
      if (stampPointType === 'STAMP_POINT') return 'spot_detail.acquire_error.stamp_point_already_acquired'
      return 'spot_detail.acquire_error.stamp_already_acquired'
    case 'CAMPAIGN_PERIOD_OUT':
      return 'spot_detail.acquire_error.campaign_period_out'
    case 'STAMP_PERIOD_OUT':
      return 'spot_detail.acquire_error.stamp_period_out'
    case 'STAMP_HOURS_OUT':
      return 'spot_detail.acquire_error.stamp_hours_out'
    case 'PERIOD_OUT':
      return 'spot_detail.acquire_error.period_out'
    case 'STAMP_NOT_AVAILABLE':
      return 'spot_detail.acquire_error.stamp_not_available'
    case 'STAMP_LOCK_FAILED':
      return 'spot_detail.acquire_error.stamp_lock_failed'
    default:
      return 'spot_detail.acquire_error.message'
  }
}

export const SpotDetailPage = ({ id }: { id: number }) => {
  const router = useRouter()
  const { gen, loginUrl, campaignSlug } = useCampaignSlug()
  const { i18n, t } = useTranslation()

  const appActorRef = AppMachineContext.useActorRef()
  const campaignData = useSelector(appActorRef, (s) => s.context.campaignData)

  const { stamp, attached } = useSpotDetailStateMachine(id, {
    isBarCodeWithGPS: campaignData?.isBarCodeWithGPS ?? false,
    isQRCodeWithGPS: campaignData?.isQRCodeWithGPS ?? false,
    isKeywordWithGPS: campaignData?.isKeywordWithGPS ?? false,
  })
  const idToken = useTokenStore((s) => s.idToken)
  const tokenLoading = useTokenStore((s) => s.loading)
  const suppressPageReload = useTokenStore((s) => s.suppressPageReload)
  const restorePageReload = useTokenStore((s) => s.restorePageReload)
  const clearShouldReloadAfterRefresh = useTokenStore((s) => s.clearShouldReloadAfterRefresh)
  const campaign = {
    type: (campaignData?.type ?? 1) as 1 | 2 | 3,
    stampShape: (campaignData?.stampShape ?? 0) as 0 | 1,
    stampPopup: undefined,
  }

  // シート種別は spot.sheetTypes から取得（スポット未取得時は campaignData.sheetTypes にフォールバック）
  const spotSheetTypes = stamp.context.spot?.sheetTypes ?? campaignData?.sheetTypes ?? []
  const { type: stampPointType, label: stampPointLabel } = useStampPointLabelFromSheetTypes(spotSheetTypes)

  const [arPhotoOpen, setArPhotoOpen] = useState(false)
  const [photoFrames, setPhotoFrames] = useState<PhotoFrameItem[]>([])
  // 音声ガイドAPI呼び出し中の二重送信防止（描画不要なため useRef で管理）
  const audioGuideInFlightRef = useRef(false)
  const [audioGuideItems, setAudioGuideItems] = useState<{ caption: string | null; audio: string | null }[]>([])
  const [activeTab, setActiveTab] = useState(0)
  const [couponUseId, setCouponUseId] = useState<number | null>(null)
  const [couponRedeemLoading, setCouponRedeemLoading] = useState(false)
  const [couponRedeemError, setCouponRedeemError] = useState<string | null>(null)
  const [couponLoginRequired, setCouponLoginRequired] = useState(false)
  const [couponUsedDialogOpen, setCouponUsedDialogOpen] = useState(false)
  const [showOcrReadingGuide, setShowOcrReadingGuide] = useState(false)

  // ── fromQr 自動起動 ────────────────────────────────────────────────────
  // 外部カメラで QR を読み取った場合、QR ページから fromQr パラメータが渡される
  // Next.js Pages Router は router.query を自動デコードするため decodeURIComponent は不要
  const fromQrHash = router.isReady && typeof router.query.fromQr === 'string' ? router.query.fromQr : null

  // スタンプ押印可能期間外かどうかを判定（外部カメラ自動起動前チェック用を兼ねる）
  // スタンプ個別の startAt/endAt を優先し、null の場合はキャンペーン期間にフォールバック。
  // open/close による時間帯制限も考慮する（null または 00:00:00 なら全日可）。
  // ここで stamp.context.spot を直接参照するのは spot エイリアスの前方定義を避けるため。
  const isOutOfPlayingPeriod = (() => {
    const now = new Date()
    const stamps = stamp.context.spot?.stamps ?? []
    const stamp0 = getActiveStamp(stamps)

    // 複数スタンプがある場合に期間内のスタンプが見つからなければ期間外
    if (stamps.length > 1 && stamp0 === null) return true

    // 1. 日付範囲チェック
    const startAt = stamp0?.startAt ?? campaignData?.playingStartAt
    const endAt = stamp0?.endAt ?? campaignData?.playingEndAt
    if (startAt != null && now < new Date(startAt)) return true
    if (endAt != null && now > new Date(endAt)) return true

    // 2. 時間帯チェック（open / close）
    // バックエンドの isWithinBusinessHours() に合わせた判定:
    // - 両方 null → 全日 OK
    // - 両方 '00:00:00' → 全日 OK（管理画面の「全日」設定）
    // - それ以外: '00:00:00' を 0 秒（深夜0時）として扱い、null は制限なしとして比較
    const open = stamp0?.open
    const close = stamp0?.close
    const isAllDayOk = (open == null && close == null) || (open === '00:00:00' && close === '00:00:00')

    if (!isAllDayOk) {
      // HH:MM:SS を秒数に変換。フォーマット不正（NaN混入防止）の場合は null を返す
      const toSecs = (hms: string): number | null => {
        if (!/^\d{2}:\d{2}:\d{2}$/.test(hms)) return null
        const [h, m, s] = hms.split(':').map(Number)
        return h * 3600 + m * 60 + s
      }
      const nowSecs = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds()
      // null → 制限なし、'00:00:00' → 0秒（深夜0時）として扱う
      const openSecs = open != null ? toSecs(open) : null
      const closeSecs = close != null ? toSecs(close) : null

      if (openSecs !== null && closeSecs !== null) {
        // 両方設定
        if (openSecs < closeSecs) {
          // 通常営業（例: 00:00:00〜18:00:00 や 09:00:00〜18:00:00）
          if (nowSecs < openSecs || nowSecs > closeSecs) return true
        } else {
          // 日跨ぎ（例: 22:00:00〜06:00:00）
          if (nowSecs < openSecs && nowSecs > closeSecs) return true
        }
      } else if (openSecs !== null) {
        // open のみ設定（close なし = 制限なし）
        if (nowSecs < openSecs) return true
      } else if (closeSecs !== null) {
        // close のみ設定（open なし = 制限なし）
        if (nowSecs > closeSecs) return true
      }
    }

    return false
  })()

  // 自動起動済みフラグ（レンダー間で保持するため useRef を使用）
  const autoStampTriggeredRef = useRef(false)
  // QR_READ 送信済みフラグ（fromQr をURL除去後も再送信しないよう保持）
  const autoQrReadTriggeredRef = useRef(false)

  // ① stamp.isIdle になったら START_STAMP_FLOW を自動送信
  // 外部カメラ遷移時は期間外チェックを行い、期間外の場合は自動起動しない
  useEffect(() => {
    if (!fromQrHash) return
    if (autoStampTriggeredRef.current) return
    if (!stamp.isIdle) return

    // 期間外の場合はスタンプフローを起動せず fromQr をURLから除去して終了
    if (isOutOfPlayingPeriod) {
      const { fromQr: _removed, ...restQuery } = router.query
      void router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true })
      return
    }

    autoStampTriggeredRef.current = true
    stamp.send({ type: 'START_STAMP_FLOW' })
  }, [fromQrHash, stamp.isIdle, stamp.send, isOutOfPlayingPeriod, router])

  // ② stamp.isQrScan になったら QR_READ を自動送信（カメラを開かずにハッシュを直接渡す）
  // 送信後は fromQr をURLから除去して、以降の手動スキャンを阻害しないようにする
  useEffect(() => {
    if (!fromQrHash) return
    if (autoQrReadTriggeredRef.current) return
    if (!stamp.isQrScan) return

    autoQrReadTriggeredRef.current = true
    stamp.send({ type: 'QR_READ', code: fromQrHash })
    // fromQr クエリを除去（shallow replace でページ再レンダーを最小化）
    const { fromQr: _removed, ...restQuery } = router.query
    void router.replace({ pathname: router.pathname, query: restQuery }, undefined, { shallow: true })
  }, [fromQrHash, stamp.isQrScan, stamp.send, router])
  // ─────────────────────────────────────────────────────────────────────

  const api = useApi()
  const queryClient = useQueryClient()

  const spot = stamp.context.spot

  // クーポン取得（spot.coupon=true の場合のみ）
  // ログイン状態に応じて Authorization ヘッダーの付与を切り替える。
  // tokenLoading 中は enabled を false にして、ゲストリクエストが先走るのを防ぐ。
  const isAuthed = idToken !== null
  const { data: couponsData } = useQuery<SpotCouponsResponse | undefined>({
    queryKey: ['spot-coupons', id, isAuthed],
    queryFn: () =>
      spot
        ? isAuthed
          ? api.auth.fetchSpotCoupons(spot.id)
          : api.guest.fetchSpotCoupons(spot.id)
        : Promise.resolve(undefined),
    enabled: !!spot?.coupon && !tokenLoading,
  })
  const coupons: SpotCouponEntryData[] = couponsData?.coupons ?? []

  // スタンプオーバーレイ用の画像を取得
  const stampId = getActiveStamp(spot?.stamps ?? [])?.id
  const stampDetailFetch = useTanstackStampDetailFetchFn(stampId ?? 0)
  const { data: stampDetailData } = useQuery({
    ...stampDetailFetch,
    enabled: !!stampId,
  })
  const stampImageUrl = stampDetailData?.stamp?.image ?? undefined
  const nonStampImageUrl = campaignData?.nonStampImage ?? null

  // tokenStore のロードが完了したら認証状態をマシンに送信（waitingForAuth での待機用）
  useEffect(() => {
    if (tokenLoading) return
    stamp.send({ type: 'AUTH_READY', isAuthenticated: idToken !== null })
  }, [tokenLoading, idToken, stamp.send])

  // GPS範囲外エラーSnackbarを4秒後に自動消去
  useEffect(() => {
    if (!stamp.isGpsRangeError) return
    const timer = setTimeout(() => stamp.send({ type: 'GPS_RANGE_ERROR_CLOSE' }), 4000)
    return () => clearTimeout(timer)
  }, [stamp.isGpsRangeError, stamp.send])

  // スタンプ獲得完了時に関連キャッシュを無効化
  useEffect(() => {
    if (!stamp.isStampShowing) return
    // スポット一覧マップのピン画像を即時更新
    void queryClient.invalidateQueries({ queryKey: ['userStamps', campaignSlug] })
    // スタンプ連動クーポンのステータスを即時更新
    void queryClient.invalidateQueries({ queryKey: ['spot-coupons', id, isAuthed] })
  }, [stamp.isStampShowing, campaignSlug, id, isAuthed, queryClient])

  // 401 エラー時: tokenRefresh が waiting になったら REFRESH_COMPLETE を送信（race condition 対応）
  const isTokenRefreshWaiting = AppMachineContext.useSelector((s) => s.matches({ phase3: { tokenRefresh: 'waiting' } }))
  const stampRefreshRef = useRef({
    isTokenRefreshWaiting,
    isWaitingForRefresh: stamp.state.matches('waitingForRefresh'),
  })
  useEffect(() => {
    const prev = stampRefreshRef.current
    const isWaitingForRefresh = stamp.state.matches('waitingForRefresh')
    const current = { isTokenRefreshWaiting, isWaitingForRefresh }
    stampRefreshRef.current = current

    const tokenJustEnteredWaiting = current.isTokenRefreshWaiting && !prev.isTokenRefreshWaiting
    const stateJustEnteredWaiting = current.isWaitingForRefresh && !prev.isWaitingForRefresh

    if (
      current.isWaitingForRefresh &&
      current.isTokenRefreshWaiting &&
      (tokenJustEnteredWaiting || stateJustEnteredWaiting)
    ) {
      stamp.send({ type: 'REFRESH_COMPLETE' })
    }
  }, [isTokenRefreshWaiting, stamp.state, stamp.send])

  // OCR 読み取り失敗: OCR_FAILURE_APPLY で ocrForm へ遷移
  const handleOcrFailureApply = () => {
    const ocrFailureId = stamp.ocrFailureId
    stamp.send({ type: 'OCR_FAILURE_APPLY' })
    void router.push(gen(`/ocrForm${ocrFailureId !== null ? `?ocrId=${ocrFailureId}` : ''}`))
  }

  // OCR 注意喚起データ（API から取得）
  const ocrWarning = getActiveStamp(spot?.stamps ?? [])?.ocrWarning ?? null

  // ARカメラ使用中はページリロードを抑止する。
  // arPhotoOpen（手動ARフォト）または stamp.isPhotoPost（スタンプ取得後ARフォト）が
  // true の場合に suppressPageReload() を呼び、ARカメラ終了時に restorePageReload() と
  // clearShouldReloadAfterRefresh() を呼んでフラグを解除する。
  // これにより、ARカメラ中に 401 → token/refresh が発生しても
  // _app.tsx の window.location.reload() がスキップされる。
  const isArCameraActive = arPhotoOpen || stamp.isPhotoPost
  useEffect(() => {
    if (!isArCameraActive) return
    suppressPageReload()
    return () => {
      restorePageReload()
      // ARカメラ使用中に 401 が発生していた場合、リロードフラグを解除する。
      clearShouldReloadAfterRefresh()
    }
  }, [isArCameraActive, suppressPageReload, restorePageReload, clearShouldReloadAfterRefresh])

  // suppressPageReload() による親再レンダリング時に onClose の参照が変わると
  // ArPhotoOverlay の useEffect cleanup が history.back() を呼んでオーバーレイを
  // 意図せず閉じてしまうため、setArPhotoOpen（useState の dispatch は安定）を
  // useCallback でラップして参照を固定する。
  const handleArPhotoClose = useCallback(() => setArPhotoOpen(false), [])

  if (stamp.isLoading) {
    return (
      <>
        <Header title="スポット詳細" showBack onBack={() => router.push(gen('/spots'))} showNotification={false} />
        <main className="overflow-y-auto pb-[60px]" style={{ height: 'calc(100dvh - 58px)' }}>
          <SpotDetailSkeleton />
        </main>
        <BottomNavigation activeItem="spot" />
      </>
    )
  }

  if (stamp.isError) {
    return (
      <>
        <Header title="スポット詳細" showBack onBack={() => router.push(gen('/spots'))} showNotification={false} />
        <main className="overflow-y-auto pb-[60px]" style={{ height: 'calc(100dvh - 58px)' }}>
          <div className="flex flex-col items-center justify-center h-full gap-4 p-6 text-center">
            <p className="text-sm text-gray-600">スポット情報の取得に失敗しました</p>
            <button
              type="button"
              className="px-4 py-2 bg-main text-white rounded-full text-sm"
              onClick={() => stamp.send({ type: 'RESET' })}
            >
              再試行
            </button>
          </div>
        </main>
        <BottomNavigation activeItem="spot" />
      </>
    )
  }

  // スタンプ表示用データ
  const isLastStamp = stamp.context.currentStampIndex >= stamp.context.acquiredStamps.length - 1
  const stampDisplayImage = stampImageUrl ?? spot?.images[0] ?? null
  const stampDisplayName = spot?.name ?? ''

  const hasArPhoto = spot?.photoFrame ?? false

  // officialSns をそのまま SpotDetail へ渡す（配列の順で表示される）
  const socialLinks = spot?.officialSns

  // ARフォトボタン押下時: GPS 付きで POST して結果に応じてフレームをセットまたはロックダイアログを表示
  // stampLinked=true のスポットはバックエンドが認証ユーザーのスタンプ取得状況を確認するため、
  // ログイン済みの場合は Authorization ヘッダー付き (api.auth) で呼び出す。
  const handleArPhotoClick = async () => {
    try {
      const gps = stamp.context.gps
      const body = stamp.gpsLoaded && (gps.lat !== 0 || gps.lon !== 0) ? { lat: gps.lat, lon: gps.lon } : {}
      const res =
        idToken !== null ? await api.auth.postSpotPhotoFrames(id, body) : await api.guest.postSpotPhotoFrames(id, body)
      if (isPhotoFrameLocked(res.photoFrames)) {
        attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
      } else {
        setPhotoFrames(toPhotoFrameItems(res.photoFrames))
        setArPhotoOpen(true)
      }
    } catch {
      attached.send({ type: 'OPEN_AR_PHOTO_LOCKED' })
    }
  }

  // 音声ガイドボタン押下時: GPS 付きで POST して結果に応じてダイアログを表示またはロックダイアログを表示
  const handleAudioGuideClick = async () => {
    if (audioGuideInFlightRef.current) return
    audioGuideInFlightRef.current = true
    try {
      const gps = stamp.context.gps
      const body = stamp.gpsLoaded && (gps.lat !== 0 || gps.lon !== 0) ? { lat: gps.lat, lon: gps.lon } : {}
      const res = await api.guest.postSpotAudioGuides(id, body)
      if (!res) {
        attached.send({ type: 'OPEN_AUDIO_GUIDE_LOCKED' })
        return
      }
      const entries = res.audioGuides
      if (isAudioGuideLocked(entries)) {
        attached.send({ type: 'OPEN_AUDIO_GUIDE_LOCKED' })
      } else {
        setAudioGuideItems(toAudioGuideDialogItems(entries))
        attached.send({ type: 'OPEN_AUDIO_GUIDE' })
      }
    } catch {
      attached.send({ type: 'OPEN_AUDIO_GUIDE_LOCKED' })
    } finally {
      audioGuideInFlightRef.current = false
    }
  }

  // クイズの現在の問題（context.quizzes は初期ロード時に取得済み）
  const currentQuiz = stamp.context.quizzes[0]

  // クーポン使用確定時: API を呼び出してクーポンを消費する
  const handleCouponUse = async () => {
    if (couponUseId === null) return
    if (idToken === null) {
      setCouponLoginRequired(true)
      setCouponUseId(null)
      return
    }
    setCouponRedeemLoading(true)
    try {
      await api.auth.postSpotCouponRedeem(id, couponUseId)
      setCouponUseId(null)
      void queryClient.invalidateQueries({ queryKey: ['spot-coupons', id, true] })
      setCouponUsedDialogOpen(true)
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 409) {
          setCouponRedeemError('このクーポンはすでに使用済みか、利用上限に達しています。')
        } else if (e.status === 403) {
          setCouponLoginRequired(true)
          setCouponUseId(null)
        } else {
          setCouponRedeemError('クーポンの利用に失敗しました。しばらくしてから再度お試しください。')
        }
      } else {
        setCouponRedeemError('クーポンの利用に失敗しました。しばらくしてから再度お試しください。')
      }
    } finally {
      setCouponRedeemLoading(false)
    }
  }

  return (
    <>
      {!stamp.isOcrCapture && (
        <Header title="スポット詳細" showBack onBack={() => router.push(gen('/spots'))} showNotification={false} />
      )}
      {stamp.isOcrCapture ? (
        <>
          <OcrFrame
            onCaptured={(imageDataUrl) => {
              setShowOcrReadingGuide(false)
              stamp.send({ type: 'OCR_CAPTURED', image: imageDataUrl })
            }}
            onGoToWarning={() => setShowOcrReadingGuide(true)}
            onClose={() => {
              setShowOcrReadingGuide(false)
              stamp.send({ type: 'OCR_CAPTURE_CLOSE' })
            }}
          />
          <DialogOcrWarning
            opened={showOcrReadingGuide}
            ocrWarning={ocrWarning}
            onClose={() => setShowOcrReadingGuide(false)}
          />
        </>
      ) : (
        <main className="overflow-y-auto pb-[60px]" style={{ height: 'calc(100dvh - 58px)' }}>
          {spot ? (
            <SpotDetail
              name={spot.name}
              imageUrls={spot.images}
              address={spot.address ?? undefined}
              url={spot.url ?? undefined}
              description={spot.description ?? undefined}
              spotLocation={
                spot.latitude != null && spot.longitude != null
                  ? { latitude: spot.latitude, longitude: spot.longitude }
                  : undefined
              }
              courseNames={spot.sheets}
              canGetStamp={(spot.stamps?.length ?? 0) > 0}
              stampAcquired={stamp.context.hasStamp}
              stampOutOfPeriod={isOutOfPlayingPeriod}
              stampImageUrl={stampImageUrl}
              nonStampImageUrl={nonStampImageUrl}
              stampShape={campaign.stampShape}
              stampPointType={stampPointType}
              onStampOverlayClick={stamp.context.hasStamp ? () => stamp.send({ type: 'STAMP_REDISPLAY' }) : undefined}
              onStampGet={() => stamp.send({ type: 'START_STAMP_FLOW' })}
              stampOutOfRange={spot.isInRange === false}
              stampGpsPermissionDenied={
                stamp.context.gps.loaded &&
                stamp.context.gps.permissionDenied &&
                getActiveStamp(spot?.stamps ?? [])?.type === 'GPS'
              }
              hasAudioGuide={spot.audioGuide}
              onAudioGuideClick={handleAudioGuideClick}
              audioGuideLoading={!stamp.gpsLoaded || stamp.isGpsRefreshing}
              geoLoading={!stamp.gpsLoaded || stamp.isGpsRefreshing || tokenLoading}
              hasArPhoto={hasArPhoto}
              arPhotoLocked={stamp.context.photoFrameInitiallyStampRequired && !stamp.context.hasStamp}
              onArPhotoClick={handleArPhotoClick}
              coupons={coupons}
              isCoupons={spot.coupon}
              tags={spot.tags}
              socialLinks={socialLinks}
              onCouponUse={(couponId) => {
                if (idToken === null) {
                  setCouponLoginRequired(true)
                } else {
                  setCouponUseId(couponId)
                }
              }}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          ) : (
            <p className="text-center py-8 text-gray-500">スポットが見つかりませんでした</p>
          )}
        </main>
      )}
      {!stamp.isOcrCapture && !stamp.isOcrWarning && <BottomNavigation activeItem="spot" />}

      <SpotDetailStateMachineDebug stamp={stamp} attached={attached} />

      {/* ── スタンプ獲得フロー ── */}

      {(() => {
        const isDescriptiveType = currentQuiz?.quizType === 1
        return (
          <>
            {(stamp.isDescriptiveQuizInput || (stamp.isQuizAnswering && isDescriptiveType)) && (
              <DialogQuizKeyword
                scrollLock
                onClose={() => stamp.send({ type: 'QUIZ_CLOSE' })}
                title={currentQuiz?.title}
                question={currentQuiz?.question ?? 'キーワードを入力してください'}
                image={currentQuiz?.questionImage ?? null}
                onSubmit={(answer) => stamp.send({ type: 'TEXT_ANSWER_SUBMIT', answer })}
                isLoading={stamp.isQuizAnswering}
              />
            )}

            {(stamp.isQuizChoice || (stamp.isQuizAnswering && !isDescriptiveType)) && (
              <DialogQuizChallenge
                scrollLock
                onClose={() => stamp.send({ type: 'QUIZ_CLOSE' })}
                title={currentQuiz?.title}
                question={currentQuiz?.question ?? 'クイズに答えてください'}
                image={currentQuiz?.questionImage ?? null}
                choices={currentQuiz?.choices ?? []}
                onSelect={(choiceId) => stamp.send({ type: 'ANSWER_SUBMIT', choiceId })}
                isLoading={stamp.isQuizAnswering}
              />
            )}
          </>
        )
      })()}

      {stamp.isQuizCorrect && (
        <DialogQuizResult
          scrollLock
          result="correct"
          heading={stamp.quizAnswerResult?.answer ?? '正解！'}
          message={stamp.quizAnswerResult?.message ?? undefined}
          image={stamp.quizAnswerResult?.correctImage ?? undefined}
          onClose={() => stamp.send({ type: 'QUIZ_CLOSE' })}
          onAction={() => stamp.send({ type: 'STAMP_GET' })}
          actionLabel={
            stampPointType === 'POINT'
              ? t('spot_detail.get_a_point', 'ポイントを獲得する')
              : stampPointType === 'STAMP_POINT'
                ? t('spot_detail.get_stamp_or_point', '獲得する')
                : t('spot_detail.get_a_stamp', 'スタンプを獲得する')
          }
        />
      )}

      {stamp.isQuizIncorrect && (
        <DialogQuizResult
          scrollLock
          result="incorrect"
          heading={
            stamp.canRetryQuiz
              ? t('spot_detail.quiz.incorrect', '不正解...')
              : t('spot_detail.quiz.daily_limit_reached', '本日の回答権を使い切りました')
          }
          message={stamp.canRetryQuiz ? (stamp.quizAnswerResult?.hintMessage ?? undefined) : undefined}
          image={stamp.quizAnswerResult?.incorrectImage ?? undefined}
          onClose={() => stamp.send({ type: 'QUIZ_CLOSE' })}
          onAction={
            stamp.canRetryQuiz ? () => stamp.send({ type: 'QUIZ_RETRY' }) : () => stamp.send({ type: 'QUIZ_CLOSE' })
          }
          actionLabel={
            stamp.canRetryQuiz ? t('spot_detail.quiz.retry', 'もう一度') : t('spot_detail.quiz.close', 'とじる')
          }
        />
      )}

      {/* 日次制限エラーダイアログ */}
      <ActionDialog
        selectorState={stamp.isQuizDailyLimitError}
        variant="message"
        title={t('spot_detail.quiz.title', 'クイズ')}
        description={t(
          'spot_detail.quiz.daily_limit_exhausted_message',
          '本日のクイズ回答は終了しました。<br>また明日チャレンジしてください。',
        )}
        cancelLabel={t('spot_detail.quiz.daily_limit_close', 'とじる')}
        closeAction={() => stamp.send({ type: 'QUIZ_DAILY_LIMIT_CLOSE' })}
      />

      {/* QR コード読み取り */}
      {/* fromQrHash がある場合はカメラオーバーレイを開かず、useEffect で直接 QR_READ を送信する */}
      <ScanCodeOverlay
        opened={stamp.isQrScan && !fromQrHash}
        mode="qr"
        onClose={() => stamp.send({ type: 'QR_CLOSE' })}
        onRead={async (result) => {
          const hash = extractQrHash(result.getText())
          if (hash === null) {
            stamp.send({ type: 'QR_WRONG_FORMAT' })
            return true
          }
          stamp.send({ type: 'QR_READ', code: hash })
          return true
        }}
      />

      {/* バーコード読み取り */}
      <ScanCodeOverlay
        opened={stamp.isBarcodeScan}
        mode="barcode"
        onClose={() => stamp.send({ type: 'BARCODE_CLOSE' })}
        onRead={async (result) => {
          const code = result.getText()
          if (!code) return false
          stamp.send({ type: 'BARCODE_READ', code })
          return true
        }}
      />

      {/* OCR 注意喚起ダイアログ */}
      <DialogOcrWarning
        opened={stamp.isOcrWarning}
        ocrWarning={ocrWarning}
        onConfirm={() => stamp.send({ type: 'OCR_WARNING_CONFIRM' })}
        onClose={() => stamp.send({ type: 'OCR_WARNING_CLOSE' })}
      />

      {/* OCR 読み取り失敗ダイアログ */}
      {stamp.isOcrReadFailure && (
        <DialogOcrReadFailure
          scrollLock
          onRetry={() => stamp.send({ type: 'OCR_FAILURE_RETRY' })}
          onApply={handleOcrFailureApply}
          onClose={() => stamp.send({ type: 'OCR_FAILURE_CLOSE' })}
        />
      )}

      {/* API呼び出し中ローディング */}
      {stamp.isAcquiring && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
            <Loader size="lg" color="var(--main-color)" />
            <p className="text-sm text-gray-600">
              {stampPointType === 'POINT'
                ? t('spot_detail.acquiring_point', 'ポイントを獲得中...')
                : t('spot_detail.acquiring_stamp', 'スタンプを獲得中...')}
            </p>
          </div>
        </div>
      )}

      {/* スタンプ獲得完了ダイアログ */}
      <DialogGetStamps
        opened={stamp.isStampShowing}
        campaign={campaign}
        image={stampDisplayImage}
        title={
          stampPointType === 'POINT'
            ? t('spot_detail.point_acquire.you_have_acquired', 'ポイントを獲得しました！')
            : t('spot_detail.stamp_acquire.you_have_acquired', 'スタンプを獲得しました！')
        }
        onClose={isLastStamp ? () => stamp.send({ type: 'STAMP_NEXT' }) : undefined}
        closeButtonOff={!isLastStamp}
      >
        <div className="mt-6 flex justify-center">
          <Button
            label={isLastStamp ? '閉じる' : '次へ'}
            variant="system"
            size="md"
            round
            onClick={() => stamp.send({ type: 'STAMP_NEXT' })}
            className="!w-[254px]"
          />
        </div>
      </DialogGetStamps>

      {/* 特典獲得 Snackbar + ARフォト利用可能通知 Snackbar（縦並び・✕ボタン押下まで表示） */}
      {(stamp.isRewardShowing || stamp.isArPhotoUnlockedShowing) && (
        <div className="fixed bottom-20 left-0 right-0 z-50 px-4 flex flex-col gap-2">
          {stamp.isRewardShowing && stamp.context.acquiredRewards.length > 0 && (
            <Snackbar
              size="withButton"
              message={t('spot_detail.reward_snackbar.message', {
                count: stamp.context.acquiredRewards.length,
              })}
              actionLabel={t('spot_detail.reward_snackbar.action', '確認する')}
              onClose={() => stamp.send({ type: 'REWARD_CLOSE' })}
              onAction={() => {
                stamp.send({ type: 'REWARD_CLOSE' })
                void router.push(gen('/rewards'))
              }}
            />
          )}
          {stamp.isArPhotoUnlockedShowing && (
            <Snackbar
              size="middle"
              message={t('spot_detail.ar_photo_unlocked.message')}
              onClose={() => stamp.send({ type: 'AR_PHOTO_UNLOCKED_CLOSE' })}
            />
          )}
        </div>
      )}

      {/* QRコード不正エラーダイアログ */}
      <ActionDialog
        selectorState={stamp.isQrWrongFormat}
        variant="message"
        description={t('spot_detail.qr_wrong_format.message', '正しいQRコードを読み込んでください。')}
        cancelLabel={t('common.close', '閉じる')}
        closeAction={() => {
          stamp.send({ type: 'QR_WRONG_FORMAT_CLOSE' })
        }}
      />

      {/* スタンプ獲得APIエラーダイアログ */}
      <ActionDialog
        selectorState={stamp.isAcquireError}
        variant="message"
        description={t(resolveAcquireErrorKey(stamp.context.errorMessage, stampPointType), { what: stampPointLabel })}
        cancelLabel={t('common.close', '閉じる')}
        closeAction={() => {
          stamp.send({ type: 'ACQUIRE_ERROR_CLOSE' })
        }}
      />

      {/* GPS範囲外エラー Snackbar（画面下部・4秒後自動消去） */}
      {stamp.isGpsRangeError && (
        <div className="fixed bottom-20 left-0 right-0 z-50 px-4">
          <Snackbar
            size="withButton"
            message={
              stampPointType === 'POINT'
                ? t(
                    'spot_detail.gps_range_error.point',
                    'ポイント獲得可能な範囲内にいません。スポットに近づいてから再度お試しください。',
                  )
                : t(
                    'spot_detail.gps_range_error.stamp',
                    'スタンプ獲得可能な範囲内にいません。スポットに近づいてから再度お試しください。',
                  )
            }
            onClose={() => stamp.send({ type: 'GPS_RANGE_ERROR_CLOSE' })}
            actionLabel="再試行"
            onAction={() => stamp.send({ type: 'GPS_RANGE_ERROR_RETRY' })}
          />
        </div>
      )}

      {/* フォトアップロード */}
      {stamp.isPhotoPost && (
        <ArPhotoOverlay
          open
          photoFrames={photoFrames}
          onClose={() => stamp.send({ type: 'PHOTO_CLOSE' })}
          onSaveSuccess={() => stamp.send({ type: 'PHOTO_CLOSE' })}
          snsHashtag=""
        />
      )}

      {/* 再表示: スタンプダイアログ */}
      <DialogRedisplayStamps
        opened={stamp.isRedisplayStampShowing}
        campaign={campaign}
        image={stampDisplayImage}
        stampName={stampDisplayName}
        isLastStamp={isLastStamp}
        title={t('spot_detail.show_acquired_stamp_again', '獲得済みスタンプ')}
        onClose={() => stamp.send({ type: 'STAMP_REDISPLAY_STAMP_NEXT' })}
        onAllClose={() => stamp.send({ type: 'STAMP_REDISPLAY_STAMP_NEXT' })}
      />

      {/* ── 付帯ダイアログ（音声ガイド・ARフォト） ── */}

      {attached.isArPhotoLocked && (
        <DialogArPhotoLocked scrollLock stampLabel={stampPointLabel} onClose={() => attached.send({ type: 'CLOSE' })} />
      )}

      {stamp.isNotAuthenticatedError && (
        <DialogLoginRequired
          opened
          scrollLock
          loginUrl={loginUrl}
          onClose={() => stamp.send({ type: 'NOT_AUTH_ERROR_CLOSE' })}
        />
      )}
      {attached.isAudioGuide && (
        <DialogSpotsAudioGuide
          scrollLock
          onClose={() => {
            attached.send({ type: 'CLOSE' })
            setAudioGuideItems([])
          }}
          audioGuides={audioGuideItems}
        />
      )}
      {attached.isAudioGuideLocked && (
        <DialogAudioGuideLocked scrollLock onClose={() => attached.send({ type: 'CLOSE' })} />
      )}

      {(() => {
        const selectedCoupon = coupons.find((c) => c.id === couponUseId)
        return (
          <DialogUseCoupon
            opened={couponUseId !== null}
            onClose={() => {
              if (!couponRedeemLoading) setCouponUseId(null)
            }}
            onUseCoupon={handleCouponUse}
            couponName={selectedCoupon?.name ?? ''}
            couponImage={selectedCoupon?.image}
            usagePeriodFrom={
              selectedCoupon?.startAt
                ? (time({ time: selectedCoupon.startAt, language: i18n.language }) ?? undefined)
                : undefined
            }
            usagePeriodTo={
              selectedCoupon?.endAt
                ? (time({ time: selectedCoupon.endAt, language: i18n.language }) ?? undefined)
                : undefined
            }
            title={t('mynavi.dialog.staff_confirmation_title', 'スタッフ確認用画面')}
            warningLine1={t('reward_detail.coupon.before_press_button_01', 'クーポンを使用するときに必ずスタッフに')}
            warningLine2={t('reward_detail.coupon.before_press_button_02', 'この画面を見せてください。')}
            swipeText={t('mynavi.dialog.swipe_to_use_coupon', '右にスワイプで使用')}
            isLoading={couponRedeemLoading}
          />
        )
      })()}

      {couponLoginRequired && (
        <DialogLoginRequired opened scrollLock loginUrl={loginUrl} onClose={() => setCouponLoginRequired(false)} />
      )}

      {/* クーポン使用完了ダイアログ */}
      <DialogCouponUsed opened={couponUsedDialogOpen} onClose={() => setCouponUsedDialogOpen(false)} />

      {couponRedeemError !== null && (
        <div className="fixed bottom-20 left-0 right-0 z-50 px-4">
          <Snackbar message={couponRedeemError} onClose={() => setCouponRedeemError(null)} />
        </div>
      )}

      {/* 手動ARフォト（スタンプ取得後フローとは独立） */}
      <ArPhotoOverlay
        open={arPhotoOpen}
        photoFrames={photoFrames}
        onClose={handleArPhotoClose}
        onSaveSuccess={handleArPhotoClose}
        snsHashtag=""
      />
    </>
  )
}
